import { useEffect, useState } from 'react';

/**
 * HEIC / HEIF — what an iPhone saves by default.
 *
 * Only Safari can DRAW one. Everywhere else (Chrome, Edge, Firefox, every
 * Android browser) an `<img src=blob:…heic>` loads nothing and paints an empty
 * box, which is how an upload "sometimes" had no preview: fine on a Mac, blank
 * on Windows or a phone. The processing engines never hit this because each one
 * converts HEIC before drawing — the previews used the raw file.
 *
 * Detection has to look at the name as well as the type: Chrome on Windows
 * reports `file.type === ''` for .heic, and Android pickers sometimes hand over
 * `image/heic-sequence` (a burst / Live Photo container) or a `.hif` name.
 */
const HEIC_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);
const HEIC_EXT = /\.(heic|heif|hif)$/i;

export function isHeicFile(file) {
    if (!file) return false;
    return HEIC_TYPES.has((file.type || '').toLowerCase()) || HEIC_EXT.test(file.name || '');
}

/**
 * TIFF (a scanner's output) and SVG (a drawing, not pixels) are the other two
 * inputs a canvas cannot take straight: no browser draws a TIFF, and an `<svg>`
 * has to be given a size before it can be rasterised. Both are turned into PNG
 * here, the way HEIC becomes JPEG, so every tool downstream sees ordinary pixels.
 */
const TIFF_TYPES = new Set(['image/tiff', 'image/x-tiff']);
const TIFF_EXT = /\.tiff?$/i;
const SVG_EXT = /\.svg$/i;

export function isTiffFile(file) {
    if (!file) return false;
    return TIFF_TYPES.has((file.type || '').toLowerCase()) || TIFF_EXT.test(file.name || '');
}

export function isSvgFile(file) {
    if (!file) return false;
    return (file.type || '').toLowerCase() === 'image/svg+xml' || SVG_EXT.test(file.name || '');
}

/** Whether this file has to be decoded before anything can draw it. */
export function needsDecoding(file) {
    return isHeicFile(file) || isTiffFile(file) || isSvgFile(file);
}

function canvasToPng(canvas) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))), 'image/png');
    });
}

/** First page of a TIFF, through UTIF (the print editor's decoder). */
async function tiffToPng(file) {
    const UTIF = (await import('utif')).default;
    const buffer = await file.arrayBuffer();
    const pages = UTIF.decode(buffer);
    if (!pages.length) throw new Error('empty tiff');
    const page = pages[0];
    UTIF.decodeImage(buffer, page, pages);
    const rgba = UTIF.toRGBA8(page);
    const canvas = document.createElement('canvas');
    canvas.width = page.width;
    canvas.height = page.height;
    canvas.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(rgba), page.width, page.height), 0, 0);
    return canvasToPng(canvas);
}

/**
 * The drawing's own size, from width/height or the viewBox. A drawing that
 * states neither is rendered at 1024px wide — big enough to stay sharp, and the
 * tools resize from there anyway.
 */
function svgSize(markup) {
    const tag = /<svg\b[^>]*>/i.exec(markup)?.[0] || '';
    const attr = (name) => {
        const raw = new RegExp(`\\b${name}\\s*=\\s*["']([^"']+)["']`, 'i').exec(tag)?.[1];
        const value = parseFloat(raw || '');
        return Number.isFinite(value) && value > 0 ? value : 0;
    };
    let width = attr('width');
    let height = attr('height');
    if (!width || !height) {
        const box = /\bviewBox\s*=\s*["']([^"']+)["']/i.exec(tag)?.[1]?.trim().split(/[\s,]+/).map(Number);
        if (box?.length === 4 && box[2] > 0 && box[3] > 0) {
            const ratio = box[3] / box[2];
            width = width || (height ? height / ratio : 1024);
            height = height || width * ratio;
        }
    }
    return { width: Math.round(width || 1024), height: Math.round(height || width || 1024) };
}

/**
 * A drawing is rendered at its own size, and a small one is drawn larger (up to
 * 2048 on the long side): it is vector, so that costs nothing in quality, and a
 * 200px raster of a logo would be useless. The size in the markup is used rather
 * than the `<img>`'s — a drawing with only a viewBox reports the browser's
 * default 300×150 box instead of its real proportions.
 */
const SVG_MIN_LONG_SIDE = 2048;

async function svgToPng(file) {
    const markup = await file.text();
    const { width, height } = svgSize(markup);
    const scale = Math.max(1, Math.min(8, SVG_MIN_LONG_SIDE / Math.max(width, height)));
    const url = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml' }));
    try {
        const img = new Image();
        await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = () => reject(new Error('svg load failed'));
            img.src = url;
        });
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        return await canvasToPng(canvas);
    } finally {
        URL.revokeObjectURL(url);
    }
}

/**
 * One conversion per file, shared by every preview that shows it. heic2any is a
 * few hundred ms per photo; a grid, a lightbox and a before/after pane asking for
 * the same picture must not each pay that again.
 */
const cache = new WeakMap();

/**
 * A blob any browser can draw: HEIC → JPEG, everything else untouched. Resolves
 * to the original file if the conversion fails, so a broken HEIC still gets a
 * (Safari-only) chance instead of an exception inside a render effect.
 */
export function toDisplayableBlob(file) {
    if (!needsDecoding(file)) return Promise.resolve(file);
    if (cache.has(file)) return cache.get(file);
    const job = (async () => {
        try {
            if (isTiffFile(file)) return await tiffToPng(file);
            if (isSvgFile(file)) return await svgToPng(file);
            const heic2any = (await import('heic2any')).default;
            const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
            return Array.isArray(converted) ? converted[0] : converted;
        } catch {
            return file;
        }
    })();
    cache.set(file, job);
    return job;
}

/**
 * For single-picture previews written as `useEffect(() => { … }, [file])`:
 * sets a drawable URL for `file` and returns the effect's cleanup. Non-HEIC files
 * get their URL synchronously (no flicker); HEIC clears the old picture, then
 * shows the decoded one.
 *
 *   useEffect(() => (file ? attachDisplayUrl(file, setUrl) : setUrl(null)), [file]);
 */
export function attachDisplayUrl(file, setUrl) {
    if (!needsDecoding(file)) {
        const url = URL.createObjectURL(file);
        setUrl(url);
        return () => URL.revokeObjectURL(url);
    }
    let url = null;
    let cancelled = false;
    setUrl(null);
    toDisplayableBlob(file).then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setUrl(url);
    });
    return () => {
        cancelled = true;
        if (url) URL.revokeObjectURL(url);
    };
}

/**
 * Before/after URL pairs for the shared lightbox (`ImagePreviewModal`), one per
 * result. The "before" is the uploaded file, so it is the one that can be HEIC;
 * it fills in once decoded while the "after" (always a browser-made blob) shows
 * straight away.
 */
export function useCompareUrls(results, files) {
    const [pairs, setPairs] = useState([]);

    useEffect(() => {
        if (!results.length) {
            setPairs([]);
            return undefined;
        }
        let cancelled = false;
        const created = [];
        const urlFor = (blob) => {
            const url = URL.createObjectURL(blob);
            created.push(url);
            return url;
        };

        setPairs(results.map((result, i) => ({
            after: result ? urlFor(result.blob) : null,
            before: files[i] && !needsDecoding(files[i]) ? urlFor(files[i]) : null,
        })));

        results.forEach((result, i) => {
            const file = files[i];
            if (!result || !file || !needsDecoding(file)) return;
            toDisplayableBlob(file).then((blob) => {
                if (cancelled) return;
                const url = urlFor(blob);
                setPairs((prev) => prev.map((pair, k) => (k === i ? { ...pair, before: url } : pair)));
            });
        });

        return () => {
            cancelled = true;
            created.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [results, files]);

    return pairs;
}

/**
 * Object URLs for a list of blobs/files, HEIC decoded first. `null` entries stay
 * `null`; URLs are revoked when the inputs change or the component unmounts.
 * `signature` is the caller's cheap identity for the list (names + sizes), so the
 * effect does not re-run on every render of a fresh array.
 */
export function useDisplayUrls(blobs, signature) {
    const [urls, setUrls] = useState([]);

    useEffect(() => {
        let cancelled = false;
        let created = [];
        // Everything that needs no decoding shows at once; HEIC tiles fill in as
        // their conversion finishes instead of holding the whole grid back.
        const instant = blobs.map((blob) => (blob && !needsDecoding(blob) ? URL.createObjectURL(blob) : null));
        created = instant.filter(Boolean);
        setUrls(instant);

        blobs.forEach((blob, index) => {
            if (!blob || !needsDecoding(blob)) return;
            toDisplayableBlob(blob).then((displayable) => {
                if (cancelled) return;
                const url = URL.createObjectURL(displayable);
                created.push(url);
                setUrls((prev) => {
                    const next = [...prev];
                    next[index] = url;
                    return next;
                });
            });
        });

        return () => {
            cancelled = true;
            created.forEach((url) => URL.revokeObjectURL(url));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);

    return urls;
}
